import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Linkedin } from "lucide-react";

const ADVANCED_SCAN_TYPES = ["apollo", "audiencelab"];
const isAdvancedScan = (t) => ADVANCED_SCAN_TYPES.includes(t);
const scanTypeLabel = (t) => {
  const map = { apollo: "Advanced", audiencelab: "Advanced", discover: "Discover", manual: "Manual", scheduled: "Scheduled" };
  return map[t] || (t ? t.charAt(0).toUpperCase() + t.slice(1) : "Manual");
};

function Scans() {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScan, setSelectedScan] = useState(null);
  const [scanDetails, setScanDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    loadScans();
    const interval = setInterval(loadScans, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const loadScans = async () => {
    try {
      const response = await axios.get(`${API}/scans`);
      setScans(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error loading scans:", error);
      toast.error("Failed to load scan history");
      setLoading(false);
    }
  };

  const loadScanDetails = async (scanId) => {
    setLoadingDetails(true);
    try {
      // Fetch scan info
      const scanResponse = await axios.get(`${API}/scans/${scanId}`);
      
      // Fetch businesses for this scan
      const businessesResponse = await axios.get(`${API}/businesses?scan_id=${scanId}&limit=1000`);
      
      setScanDetails({
        scan: scanResponse.data,
        businesses: businessesResponse.data
      });
      setShowDetailsModal(true);
    } catch (error) {
      console.error("Error loading scan details:", error);
      toast.error("Failed to load scan details");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleRowClick = (scan) => {
    setSelectedScan(scan);
    loadScanDetails(scan.id);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const calculateDuration = (scan) => {
    if (!scan.started_at) return "N/A";
    const start = new Date(scan.started_at);
    const end = scan.completed_at ? new Date(scan.completed_at) : new Date();
    const diff = Math.floor((end - start) / 1000); // seconds
    
    if (diff < 60) return `${diff}s`;
    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;
    return `${minutes}m ${seconds}s`;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading scans...</p>
      </div>
    );
  }

  return (
    <div data-testid="scans-page">
      <div className="page-header">
        <h1 className="page-title">Scan History</h1>
        <p className="page-description">
          View all automated and manual scan executions. Click a row to see details.
        </p>
      </div>

      <div className="content-card">
        {scans.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-title">No scans yet</div>
            <div className="empty-state-description">
              Start your first scan from the dashboard
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="data-table" data-testid="scans-table" style={{ minWidth: '700px' }}>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th>Businesses</th>
                  <th>Emails</th>
                  <th>Synced</th>
                  <th>Errors</th>
                  <th>Tag</th>
                </tr>
              </thead>
              <tbody>
                {scans.map((scan) => (
                  <tr 
                    key={scan.id} 
                    data-testid={`scan-row-${scan.id}`}
                    onClick={() => handleRowClick(scan)}
                    style={{ cursor: 'pointer' }}
                    className="hover:bg-gray-50"
                  >
                    <td>
                      <span className={`status-badge status-${scan.status}`} style={{ fontSize: '10px', padding: '3px 8px' }}>
                        {scan.status}
                      </span>
                    </td>
                    <td>
                      {isAdvancedScan(scan.trigger_type) ? (
                        <span
                          data-testid={`scan-type-${scan.id}`}
                          style={{ background: '#ede9fe', color: '#6d28d9', fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px', whiteSpace: 'nowrap' }}
                        >
                          Advanced
                        </span>
                      ) : (
                        <span data-testid={`scan-type-${scan.id}`} style={{ textTransform: 'capitalize', fontSize: '13px' }}>
                          {scanTypeLabel(scan.trigger_type)}
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(scan.started_at)}</td>
                    <td style={{ fontSize: '13px' }}>{calculateDuration(scan)}</td>
                    <td><strong>{scan.businesses_found}</strong></td>
                    <td><strong>{scan.emails_found}</strong></td>
                    <td><strong>{scan.contacts_synced}</strong></td>
                    <td>
                      {scan.errors > 0 ? (
                        <span style={{ color: '#dc2626' }}>{scan.errors}</span>
                      ) : (
                        <span style={{ color: '#16a34a' }}>0</span>
                      )}
                    </td>
                    <td>
                      <code style={{ fontSize: '10px', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                        {scan.tag_used || 'N/A'}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Scan Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Scan Details</DialogTitle>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="loading">
              <div className="spinner"></div>
              <p>Loading details...</p>
            </div>
          ) : scanDetails ? (
            <div>
              {/* Scan Summary */}
              <div style={{ marginBottom: '24px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: '#334155' }}>
                  Scan Summary
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', fontSize: '14px' }}>
                  <div>
                    <span style={{ color: '#64748b' }}>Scan ID:</span>{' '}
                    <code style={{ fontSize: '12px', background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px' }}>
                      {scanDetails.scan.id}
                    </code>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Status:</span>{' '}
                    <span className={`status-badge status-${scanDetails.scan.status}`}>
                      {scanDetails.scan.status}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Trigger Type:</span>{' '}
                    <strong style={{ textTransform: 'capitalize' }}>{scanTypeLabel(scanDetails.scan.trigger_type)}</strong>
                    {isAdvancedScan(scanDetails.scan.trigger_type) && (
                      <span style={{ marginLeft: '8px', background: '#ede9fe', color: '#6d28d9', fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' }}>
                        Advanced Search
                      </span>
                    )}
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Started:</span>{' '}
                    <strong>{formatDate(scanDetails.scan.started_at)}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Completed:</span>{' '}
                    <strong>{formatDate(scanDetails.scan.completed_at)}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Duration:</span>{' '}
                    <strong>{calculateDuration(scanDetails.scan)}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>Tag:</span>{' '}
                    <code style={{ fontSize: '12px', background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px' }}>
                      {scanDetails.scan.tag_used}
                    </code>
                  </div>
                </div>
              </div>

              {/* Statistics */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                <div style={{ padding: '16px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                  <div style={{ fontSize: '12px', color: '#0369a1', fontWeight: '600', marginBottom: '4px' }}>BUSINESSES</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#0c4a6e' }}>{scanDetails.scan.businesses_found}</div>
                </div>
                <div style={{ padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: '12px', color: '#15803d', fontWeight: '600', marginBottom: '4px' }}>EMAILS FOUND</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#14532d' }}>{scanDetails.scan.emails_found}</div>
                </div>
                <div style={{ padding: '16px', background: '#faf5ff', borderRadius: '8px', border: '1px solid #e9d5ff' }}>
                  <div style={{ fontSize: '12px', color: '#7c3aed', fontWeight: '600', marginBottom: '4px' }}>CRM SYNCED</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#5b21b6' }}>{scanDetails.scan.contacts_synced}</div>
                </div>
                <div style={{ padding: '16px', background: scanDetails.scan.errors > 0 ? '#fef2f2' : '#f0fdf4', borderRadius: '8px', border: scanDetails.scan.errors > 0 ? '1px solid #fecaca' : '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: '12px', color: scanDetails.scan.errors > 0 ? '#991b1b' : '#15803d', fontWeight: '600', marginBottom: '4px' }}>ERRORS</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: scanDetails.scan.errors > 0 ? '#7f1d1d' : '#14532d' }}>{scanDetails.scan.errors}</div>
                </div>
              </div>

              {/* Error Messages */}
              {scanDetails.scan.error_messages && scanDetails.scan.error_messages.length > 0 && (
                <div style={{ marginBottom: '24px', padding: '16px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '8px', color: '#991b1b' }}>
                    ⚠️ Error Messages
                  </h3>
                  <ul style={{ marginLeft: '20px', fontSize: '13px', color: '#7f1d1d' }}>
                    {scanDetails.scan.error_messages.map((msg, idx) => (
                      <li key={idx} style={{ marginBottom: '4px' }}>{msg}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Businesses List */}
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px', color: '#334155' }}>
                  Businesses Found ({scanDetails.businesses.length})
                </h3>
                {scanDetails.businesses.length === 0 ? (
                  <p style={{ fontSize: '14px', color: '#64748b', textAlign: 'center', padding: '20px' }}>
                    No businesses found in this scan
                  </p>
                ) : (
                  <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                    <table className="data-table" style={{ fontSize: '13px' }}>
                      <thead>
                        <tr>
                          <th>Business Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>City</th>
                          <th>LinkedIn</th>
                          <th>Priority</th>
                          <th>CRM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scanDetails.businesses.map((business) => (
                          <tr key={business.id} data-testid={`scan-business-row-${business.id}`}>
                            <td>
                              <strong>{business.business_name}</strong>
                              {business.job_title && (
                                <>
                                  <br />
                                  <span style={{ fontSize: '11px', color: '#64748b' }} data-testid={`business-job-title-${business.id}`}>
                                    {business.job_title}
                                  </span>
                                </>
                              )}
                            </td>
                            <td>
                              {business.email ? (
                                <>
                                  {business.email}
                                  <br />
                                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                                    {business.email_confidence}% confidence
                                  </span>
                                </>
                              ) : (
                                <span style={{ color: '#94a3b8' }}>No email</span>
                              )}
                            </td>
                            <td data-testid={`business-phone-${business.id}`}>
                              {business.phone ? (
                                <span style={{ color: '#0f172a', fontWeight: 500 }}>{business.phone}</span>
                              ) : business.phone_status === 'pending' ? (
                                <span style={{ background: '#fef3c7', color: '#b45309', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', whiteSpace: 'nowrap' }}>
                                  Enriching…
                                </span>
                              ) : (
                                <span style={{ color: '#94a3b8' }}>—</span>
                              )}
                            </td>
                            <td>{business.city || 'N/A'}</td>
                            <td>
                              {business.linkedin_url ? (
                                <a
                                  href={business.linkedin_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`business-linkedin-${business.id}`}
                                  style={{ color: '#0a66c2', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
                                >
                                  <Linkedin size={16} />
                                  View
                                </a>
                              ) : (
                                <span style={{ color: '#94a3b8' }}>—</span>
                              )}
                            </td>
                            <td>
                              <span className={`status-badge priority-${(business.priority || 'medium').toLowerCase()}`}>
                                {business.priority}
                              </span>
                            </td>
                            <td>
                              {business.ghl_synced ? (
                                <span style={{ color: '#16a34a' }}>✓</span>
                              ) : (
                                <span style={{ color: '#94a3b8' }}>✗</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Scans;
